import { LivingRoom } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import CalcoloCanoneConcordatoLink from './CalcoloCanoneConcordatoLink';
import {
  StyledImage,
  StyledLinkToProperty,
  StyledListingCard,
  StyledListingCardBody,
  StyledListingCardBodyItem,
  StyledListingCardBodyItemDescription,
  StyledListingCardBodyItemTitle,
  StyledListingCardCtaButton,
  StyledListingCardCtaButtonContainer,
  StyledListingCardHeader,
  StyledListingCardImageWrapper,
  StyledListingCardmagePlaceholder,
  StyledListingCardStatus,
  StyledListingCardTitle,
} from './ListingCard.styles';
import { IListingCardProps } from './ListingCard.types';

const ListingCard = ({
  id,
  image,
  name,
  status,
  price,
  size,
  nBedrooms,
  nBathrooms,
  showButtons = true,
  province,
}: IListingCardProps): JSX.Element => {
  const { locale } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation(['listings', 'navigation', 'routes']);

  return (
    <StyledListingCard>
      <StyledListingCardHeader>
        <StyledListingCardImageWrapper>
          {image ? (
            <StyledImage src={`${process.env.REACT_APP_IMAGES_URL}/${image}`} />
          ) : (
            <StyledListingCardmagePlaceholder>
              <LivingRoom />
            </StyledListingCardmagePlaceholder>
          )}
        </StyledListingCardImageWrapper>
        <div>
          <StyledListingCardTitle>
            {name ?? `${upperFirst(t('listings:listing'))} #${id}`}
          </StyledListingCardTitle>
          <StyledListingCardStatus>
            {upperFirst(t(`listings:${status}`))}
          </StyledListingCardStatus>
          <StyledLinkToProperty
            href={`${process.env.REACT_APP_SITE_URL}/${locale}/${t(
              'routes:rent-path-param',
            )}/${id}`}
            target="_blank"
            rel="noreferrer"
          >
            {upperFirst(t('listings:seeProperty'))}
          </StyledLinkToProperty>
        </div>
        {(province?.toLowerCase() === 'mi' ||
          province?.toLowerCase() === 'to' ||
          province?.toLowerCase() === 'rm') &&
          status !== 'draft' && (
            <div>
              <CalcoloCanoneConcordatoLink property_id={id} />
            </div>
          )}
      </StyledListingCardHeader>
      <StyledListingCardBody>
        <StyledListingCardBodyItem>
          <StyledListingCardBodyItemTitle>
            {upperFirst(t('listings:price'))}
          </StyledListingCardBodyItemTitle>
          <StyledListingCardBodyItemDescription>
            {price
              ? new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'EUR',
                }).format(price)
              : '?€'}
          </StyledListingCardBodyItemDescription>
        </StyledListingCardBodyItem>
        <StyledListingCardBodyItem>
          <StyledListingCardBodyItemTitle>
            {upperFirst(t('listings:size'))}
          </StyledListingCardBodyItemTitle>
          <StyledListingCardBodyItemDescription>
            {size ?? '?'}m<sup>2</sup>
          </StyledListingCardBodyItemDescription>
        </StyledListingCardBodyItem>
        <StyledListingCardBodyItem>
          <StyledListingCardBodyItemTitle>
            {upperFirst(t('listings:rooms'))}
          </StyledListingCardBodyItemTitle>
          <StyledListingCardBodyItemDescription>
            {nBedrooms ?? '?'}
          </StyledListingCardBodyItemDescription>
        </StyledListingCardBodyItem>
        <StyledListingCardBodyItem>
          <StyledListingCardBodyItemTitle>
            {upperFirst(t('listings:bathrooms'))}
          </StyledListingCardBodyItemTitle>
          <StyledListingCardBodyItemDescription>
            {nBathrooms ?? '?'}
          </StyledListingCardBodyItemDescription>
        </StyledListingCardBodyItem>
      </StyledListingCardBody>
      {showButtons && (
        <StyledListingCardCtaButtonContainer>
          <StyledListingCardCtaButton
            user="landlord"
            variant="primary"
            tag="a"
            onClick={() => {
              navigate(`/${locale}/${t('navigation:listings')}/${id}`);
            }}
          >
            {t('listings:houseRequirements')}
          </StyledListingCardCtaButton>
          {(status === 'active' || status === 'pending') && (
            <StyledListingCardCtaButton
              user="landlord"
              variant="secondary"
              tag="a"
              href={`${process.env.REACT_APP_SITE_URL}/${locale}/edit/property/${id}`}
            >
              {t('listings:edit')}
            </StyledListingCardCtaButton>
          )}
          {status === 'draft' && (
            <StyledListingCardCtaButton
              user="landlord"
              variant="secondary"
              tag="a"
              href={`${process.env.REACT_APP_SITE_URL}/${locale}/edit/prop/draft/${id}`}
            >
              {t('listings:complete')}
            </StyledListingCardCtaButton>
          )}
          {status === 'deleted' && (
            <StyledListingCardCtaButton
              user="landlord"
              variant="secondary"
              tag="a"
              href="tel:+390294757531"
            >
              {t('listings:requestRestore')}
            </StyledListingCardCtaButton>
          )}
        </StyledListingCardCtaButtonContainer>
      )}
    </StyledListingCard>
  );
};

export default ListingCard;
